/**
 * HIGHWAY mode — infinite upward-scrolling camera; cursor lives in world space.
 * `cameraY` = world Y at the bottom edge of the viewport.
 * Screen Y = cameraY + canvasH - worldY (cursor drifts down if it does not keep up).
 */

import { distPointToSeg } from "./speedrun-levels";
import { gameRandom } from "./game-rng";

let highwayRandom: () => number = gameRandom;

/** Multiplayer: match server obstacle RNG (same seed as MpHighwaySim). */
export function setHighwayRandom(rng: () => number): void {
    highwayRandom = rng;
}

function hwRand(): number {
    return highwayRandom();
}

export type HighwayLaserSeg = { x1: number; y1: number; x2: number; y2: number };

export type HighwayObstacle =
    | {
          kind: "laser";
          segs: HighwayLaserSeg[];
          half: number;
          /** Optional horizontal sweep (screen px/s). */
          sweepVx?: number;
          sweepX?: number;
      }
    | {
          kind: "cube";
          worldX: number;
          worldY: number;
          half: number;
          vx: number;
          hue: number;
      }
    | {
          kind: "triangle";
          worldX: number;
          worldY: number;
          size: number;
          vx: number;
          hue: number;
      }
    | {
          kind: "ball";
          worldX: number;
          worldY: number;
          r: number;
          vx: number;
          hue: number;
      };

const BASE_CAMERA_PX_S_DEFAULT = 78;
/** Speed tier stops increasing at this score (5000 pts = max camera speed). */
const HIGHWAY_MAX_SPEED_SCORE_DEFAULT = 5000;
const SCORE_PER_SPEED_TIER_DEFAULT = 1000;
const SPEED_STEP_MUL_DEFAULT = 1.25;
const LASER_HALF_BASE = 5;
const SPAWN_LEAD_PX = 220;
const CULL_BELOW_PX = 160;
const ROAD_WIDTH_FRAC_DEFAULT = 0.72;
/** Min world-Y between laser rows so gap columns can align before the next bar. */
const MIN_LASER_WORLD_Y_GAP_MUL = 0.72;

let baseCameraPxS = BASE_CAMERA_PX_S_DEFAULT;
let highwayMaxSpeedScore = HIGHWAY_MAX_SPEED_SCORE_DEFAULT;
let scorePerSpeedTier = SCORE_PER_SPEED_TIER_DEFAULT;
let speedStepMul = SPEED_STEP_MUL_DEFAULT;
let roadWidthFrac = ROAD_WIDTH_FRAC_DEFAULT;

export function setHighwayTuning(opts?: {
    baseCameraPxS?: number;
    maxSpeedScore?: number;
    scorePerSpeedTier?: number;
    speedStepMul?: number;
    roadWidthFrac?: number;
}): void {
    baseCameraPxS =
        opts?.baseCameraPxS != null && Number.isFinite(opts.baseCameraPxS)
            ? Math.max(1, opts.baseCameraPxS)
            : BASE_CAMERA_PX_S_DEFAULT;
    highwayMaxSpeedScore =
        opts?.maxSpeedScore != null && Number.isFinite(opts.maxSpeedScore)
            ? Math.max(100, opts.maxSpeedScore)
            : HIGHWAY_MAX_SPEED_SCORE_DEFAULT;
    scorePerSpeedTier =
        opts?.scorePerSpeedTier != null && Number.isFinite(opts.scorePerSpeedTier)
            ? Math.max(50, opts.scorePerSpeedTier)
            : SCORE_PER_SPEED_TIER_DEFAULT;
    speedStepMul =
        opts?.speedStepMul != null && Number.isFinite(opts.speedStepMul)
            ? Math.max(1, opts.speedStepMul)
            : SPEED_STEP_MUL_DEFAULT;
    roadWidthFrac =
        opts?.roadWidthFrac != null && Number.isFinite(opts.roadWidthFrac)
            ? Math.max(0.1, Math.min(1, opts.roadWidthFrac))
            : ROAD_WIDTH_FRAC_DEFAULT;
}

let cameraY = 0;
let obstacles: HighwayObstacle[] = [];
let nextSpawnWorldY = 0;

function clamp(v: number, lo: number, hi: number) {
    return Math.max(lo, Math.min(hi, v));
}

export function speedTier(score: number): number {
    const maxTier = Math.max(1, Math.floor(highwayMaxSpeedScore / scorePerSpeedTier));
    const s = Math.min(Math.max(0, score), highwayMaxSpeedScore);
    return Math.min(maxTier, Math.floor(s / scorePerSpeedTier));
}

export function highwayCameraSpeedPxS(score: number, layoutScale: number): number {
    const tier = speedTier(score);
    const mul = Math.pow(speedStepMul, tier);
    return baseCameraPxS * mul * layoutScale;
}

export function resetHighwayState(canvasW: number, canvasH: number) {
    cameraY = 0;
    obstacles = [];
    nextSpawnWorldY = canvasH + SPAWN_LEAD_PX;
}

export function getHighwayCameraY() {
    return cameraY;
}

/** World position → canvas draw / collision coordinates. */
/** World position → canvas draw coordinates (optional ref size for multiplayer). */
export function highwayWorldToScreen(
    worldX: number,
    worldY: number,
    canvasW: number,
    canvasH: number,
    refW = canvasW,
    refH = canvasH,
) {
    const sx = canvasW / refW;
    const sy = canvasH / refH;
    return {
        x: worldX * sx,
        y: (cameraY + refH - worldY) * sy,
    };
}

export function highwayCursorScreenPos(worldX: number, worldY: number, canvasH: number) {
    return { x: worldX, y: cameraY + canvasH - worldY };
}

function worldToScreenY(worldY: number, canvasH: number) {
    return cameraY + canvasH - worldY;
}

export function highwayRoadBounds(canvasW: number) {
    const roadW = canvasW * roadWidthFrac;
    const roadX = (canvasW - roadW) / 2;
    return { left: roadX, right: roadX + roadW };
}

/** True when the cursor circle crosses the left or right track edge. */
export function highwayCursorOffTrack(cursorX: number, cursorR: number, canvasW: number): boolean {
    const { left, right } = highwayRoadBounds(canvasW);
    return cursorX - cursorR < left || cursorX + cursorR > right;
}

function spawnSpacingPx(tier: number, layoutScale: number) {
    const base = 190 - tier * 11;
    return Math.max(68, base) * layoutScale;
}

/** Extra obstacle rows per spawn wave as score tier rises (every 1000 pts, max at 5000). */
function spawnsPerWave(tier: number) {
    return Math.max(1, 1 + Math.floor(tier / 2));
}

function roadPlayableX(canvasW: number, layoutScale: number, pad = 0) {
    const inset = 10 * layoutScale + pad;
    const { left, right } = highwayRoadBounds(canvasW);
    const xMin = left + inset;
    const xMax = right - inset;
    return { xMin, xMax, width: Math.max(0, xMax - xMin) };
}

function randomRoadX(canvasW: number, layoutScale: number, pad = 0) {
    const { xMin, xMax, width } = roadPlayableX(canvasW, layoutScale, pad);
    if (width <= 0) return (xMin + xMax) / 2;
    return xMin + hwRand() * width;
}

function gapSpanPx(tier: number, layoutScale: number) {
    const base = 108 - tier * 4.5;
    return Math.max(44, base) * layoutScale;
}

function minLaserWorldYGap(tier: number, layoutScale: number) {
    return Math.max(gapSpanPx(tier, layoutScale) * 1.4, spawnSpacingPx(tier, layoutScale) * MIN_LASER_WORLD_Y_GAP_MUL);
}

function laserWorldY(o: HighwayObstacle): number | null {
    if (o.kind !== "laser") return null;
    return o.segs[0]?.y1 ?? null;
}

function tooCloseToAnotherLaser(worldY: number, tier: number, layoutScale: number, skipIndex = -1): boolean {
    const minGap = minLaserWorldYGap(tier, layoutScale);
    for (let i = 0; i < obstacles.length; i++) {
        if (i === skipIndex) continue;
        const ly = laserWorldY(obstacles[i]);
        if (ly == null) continue;
        if (Math.abs(ly - worldY) < minGap) return true;
    }
    return false;
}

function gapCenterFromHorizontalLaser(o: HighwayObstacle): number | null {
    if (o.kind !== "laser") return null;
    const ys = o.segs.map((s) => s.y1);
    if (Math.max(...ys) - Math.min(...ys) > 4) return null;
    const byX = [...o.segs].sort((a, b) => Math.min(a.x1, a.x2) - Math.min(b.x1, b.x2));
    let best = 0;
    let center: number | null = null;
    for (let i = 0; i < byX.length - 1; i++) {
        const left = Math.max(byX[i].x1, byX[i].x2);
        const right = Math.min(byX[i + 1].x1, byX[i + 1].x2);
        const w = right - left;
        if (w > best) {
            best = w;
            center = (left + right) / 2;
        }
    }
    return center;
}

function alignedGapCenterX(worldY: number, tier: number, layoutScale: number): number | null {
    const maxDy = minLaserWorldYGap(tier, layoutScale) * 1.25;
    let pick: { dy: number; cx: number } | null = null;
    for (const o of obstacles) {
        if (o.kind !== "laser") continue;
        const ly = laserWorldY(o);
        if (ly == null) continue;
        const dy = Math.abs(ly - worldY);
        if (dy > maxDy) continue;
        const cx = gapCenterFromHorizontalLaser(o);
        if (cx == null) continue;
        if (!pick || dy < pick.dy) pick = { dy, cx };
    }
    return pick?.cx ?? null;
}

function horizontalLaserGapSlots(
    xMin: number,
    xMax: number,
    usable: number,
    gap: number,
    gapCount: number,
    gapCenterX?: number | null,
) {
    const slots: { start: number; end: number }[] = [];
    if (usable <= gap + 16) {
        const g = Math.min(gap, usable * 0.42);
        const start = xMin + (usable - g) / 2;
        slots.push({ start, end: start + g });
        return slots;
    }
    if (gapCenterX != null && gapCount === 1) {
        const start = clamp(gapCenterX - gap / 2, xMin, xMax - gap);
        slots.push({ start, end: start + gap });
        return slots;
    }
    let cursor = xMin;
    for (let i = 0; i < gapCount; i++) {
        const blockW = (usable - gap * gapCount) / (gapCount + 1);
        cursor += blockW;
        slots.push({ start: cursor, end: cursor + gap });
        cursor += gap;
    }
    return slots;
}

function laserHalfPx(layoutScale: number) {
    return LASER_HALF_BASE * layoutScale;
}

function mobSpeedPxS(tier: number, layoutScale: number, fast: boolean) {
    const base = 95 * layoutScale;
    const tierMul = 1 + tier * 0.09;
    const fastMul = fast ? 1.55 + tier * 0.06 : 1;
    return base * tierMul * fastMul;
}

function randHue() {
    const hues = [175, 195, 285, 310, 45, 120];
    return hues[(hwRand() * hues.length) | 0];
}

function buildHorizontalLaserGaps(
    worldY: number,
    canvasW: number,
    tier: number,
    layoutScale: number,
    sweepVx = 0,
    gapCenterX?: number | null,
): HighwayObstacle {
    const half = laserHalfPx(layoutScale);
    const gap = gapSpanPx(tier, layoutScale);
    const gapCount = tier >= 7 ? 2 : tier >= 3 ? 1 + ((hwRand() * 2) | 0) : 1;
    const { xMin, xMax, width: usable } = roadPlayableX(canvasW, layoutScale, half);
    const slots = horizontalLaserGapSlots(xMin, xMax, usable, gap, gapCount, gapCenterX);
    const segs: HighwayLaserSeg[] = [];
    let x = xMin;
    for (const slot of slots) {
        if (slot.start - x > 8) {
            segs.push({ x1: x, y1: worldY, x2: slot.start, y2: worldY });
        }
        x = slot.end;
    }
    if (xMax - x > 8) {
        segs.push({ x1: x, y1: worldY, x2: xMax, y2: worldY });
    }
    if (segs.length === 0) {
        const g = Math.min(gap, Math.max(usable * 0.35, 28 * layoutScale));
        const start = xMin + Math.max(0, (usable - g) / 2);
        if (start > xMin + 4) segs.push({ x1: xMin, y1: worldY, x2: start, y2: worldY });
        if (xMax > start + g + 4) segs.push({ x1: start + g, y1: worldY, x2: xMax, y2: worldY });
    }
    const obs: HighwayObstacle = { kind: "laser", segs, half };
    if (sweepVx !== 0) {
        obs.sweepVx = sweepVx;
        obs.sweepX = 0;
    }
    return obs;
}

function buildVerticalLaser(worldY: number, canvasW: number, canvasH: number, tier: number, layoutScale: number): HighwayObstacle {
    const half = laserHalfPx(layoutScale);
    const gap = gapSpanPx(tier, layoutScale);
    const x = randomRoadX(canvasW, layoutScale, half);
    const span = canvasH * 0.55;
    const yTop = worldY - span * 0.35;
    const yBot = worldY + span * 0.35;
    const mid = (yTop + yBot) / 2;
    const segs: HighwayLaserSeg[] = [
        { x1: x, y1: yTop, x2: x, y2: mid - gap / 2 },
        { x1: x, y1: mid + gap / 2, x2: x, y2: yBot },
    ];
    return { kind: "laser", segs, half };
}

function spawnMobAt(worldY: number, score: number, canvasW: number, layoutScale: number, roll: number) {
    const tier = speedTier(score);
    const fastMob = hwRand() < Math.min(0.78, 0.18 + tier * 0.06);
    const mobRoll = (roll - 0.56) / 0.44;

    if (mobRoll < 0.27) {
        const half = (14 + hwRand() * 10) * layoutScale;
        const vx = (hwRand() < 0.5 ? -1 : 1) * mobSpeedPxS(tier, layoutScale, fastMob);
        obstacles.push({
            kind: "cube",
            worldX: randomRoadX(canvasW, layoutScale, half),
            worldY,
            half,
            vx,
            hue: randHue(),
        });
        return;
    }
    if (mobRoll < 0.64) {
        const size = (16 + hwRand() * 12) * layoutScale;
        const vx = (hwRand() < 0.5 ? -1 : 1) * mobSpeedPxS(tier, layoutScale, fastMob);
        obstacles.push({
            kind: "triangle",
            worldX: randomRoadX(canvasW, layoutScale, size),
            worldY,
            size,
            vx,
            hue: randHue(),
        });
        return;
    }
    const r = (12 + hwRand() * 10) * layoutScale;
    const vx = (hwRand() < 0.5 ? -1 : 1) * mobSpeedPxS(tier, layoutScale, fastMob);
    obstacles.push({
        kind: "ball",
        worldX: randomRoadX(canvasW, layoutScale, r),
        worldY,
        r,
        vx,
        hue: randHue(),
    });
}

function trySpawnLaserAt(
    worldY: number,
    score: number,
    canvasW: number,
    canvasH: number,
    layoutScale: number,
    roll: number,
    gapCenterX?: number | null,
): boolean {
    const tier = speedTier(score);
    if (tooCloseToAnotherLaser(worldY, tier, layoutScale)) return false;

    const fastMob = hwRand() < Math.min(0.78, 0.18 + tier * 0.06);
    if (roll < 0.32) {
        obstacles.push(buildHorizontalLaserGaps(worldY, canvasW, tier, layoutScale, 0, gapCenterX));
        return true;
    }
    if (roll < 0.44) {
        const vx = (hwRand() < 0.5 ? -1 : 1) * mobSpeedPxS(tier, layoutScale, fastMob);
        obstacles.push(buildHorizontalLaserGaps(worldY, canvasW, tier, layoutScale, vx, gapCenterX));
        return true;
    }
    if (roll < 0.56) {
        obstacles.push(buildVerticalLaser(worldY, canvasW, canvasH, tier, layoutScale));
        return true;
    }
    return false;
}

function spawnObstacleAt(
    worldY: number,
    score: number,
    canvasW: number,
    canvasH: number,
    layoutScale: number,
    opts?: { allowLaser?: boolean; gapCenterX?: number | null },
) {
    const allowLaser = opts?.allowLaser !== false;
    const gapCenterX = opts?.gapCenterX ?? null;
    const roll = hwRand();

    if (allowLaser && roll < 0.56) {
        if (trySpawnLaserAt(worldY, score, canvasW, canvasH, layoutScale, roll, gapCenterX)) return;
    }
    spawnMobAt(worldY, score, canvasW, layoutScale, allowLaser ? roll : 0.56 + hwRand() * 0.44);
}

function ensureSpawnAhead(score: number, canvasW: number, canvasH: number, layoutScale: number) {
    const tier = speedTier(score);
    const spacing = spawnSpacingPx(tier, layoutScale);
    const wave = spawnsPerWave(tier);
    const horizon = cameraY + canvasH + SPAWN_LEAD_PX;
    while (nextSpawnWorldY < horizon) {
        const waveBaseY = nextSpawnWorldY;
        let laserSpawnedInWave = false;
        for (let i = 0; i < wave; i++) {
            const t = wave <= 1 ? 0 : i / (wave - 1);
            const worldY = waveBaseY + spacing * (0.22 + t * 0.58);
            spawnObstacleAt(worldY, score, canvasW, canvasH, layoutScale, {
                allowLaser: !laserSpawnedInWave,
                gapCenterX: alignedGapCenterX(worldY, tier, layoutScale),
            });
            const spawned = obstacles[obstacles.length - 1];
            if (spawned?.kind === "laser") laserSpawnedInWave = true;
        }
        nextSpawnWorldY += spacing * (0.82 + hwRand() * 0.36);
    }
}

function cullObstacles() {
    const minWorldY = cameraY - CULL_BELOW_PX;
    obstacles = obstacles.filter(o => {
        const wy = o.kind === "laser" ? o.segs[0]?.y1 ?? 0 : o.worldY;
        return wy >= minWorldY;
    });
}

export function updateHighway(
    dt: number,
    score: number,
    canvasW: number,
    canvasH: number,
    layoutScale: number,
) {
    const camSpd = highwayCameraSpeedPxS(score, layoutScale);
    cameraY += camSpd * dt;

    for (const o of obstacles) {
        if (o.kind === "laser" && o.sweepVx) {
            o.sweepX = (o.sweepX ?? 0) + o.sweepVx * dt;
            const { width } = roadPlayableX(canvasW, layoutScale, 0);
            const maxShift = width * 0.18;
            if (Math.abs(o.sweepX) > maxShift) o.sweepVx = -o.sweepVx;
        } else if (o.kind === "cube" || o.kind === "triangle" || o.kind === "ball") {
            o.worldX += o.vx * dt;
            const pad =
                o.kind === "ball" ? o.r : o.kind === "cube" ? o.half : o.size;
            const { left, right } = highwayRoadBounds(canvasW);
            if (o.worldX < left + pad) {
                o.worldX = left + pad;
                o.vx = Math.abs(o.vx);
            } else if (o.worldX > right - pad) {
                o.worldX = right - pad;
                o.vx = -Math.abs(o.vx);
            }
        }
    }

    ensureSpawnAhead(score, canvasW, canvasH, layoutScale);
    cullObstacles();
}

function neon(ctx: CanvasRenderingContext2D, hue: number, alpha = 1) {
    ctx.fillStyle = `hsla(${hue}, 95%, 62%, ${alpha})`;
    ctx.strokeStyle = `hsla(${hue}, 100%, 72%, ${alpha})`;
    ctx.shadowColor = `hsla(${hue}, 100%, 55%, ${Math.min(1, alpha + 0.2)})`;
}

export function drawHighwayRoad(
    ctx: CanvasRenderingContext2D,
    canvasW: number,
    canvasH: number,
    layoutScale: number,
    accentHex: string,
) {
    const { left: roadX, right } = highwayRoadBounds(canvasW);
    const roadW = right - roadX;
    ctx.save();
    ctx.fillStyle = "rgba(8,12,22,0.92)";
    ctx.fillRect(roadX, 0, roadW, canvasH);

    const dash = 42 * layoutScale;
    const offset = cameraY % (dash * 2);
    ctx.strokeStyle = "rgba(255,255,255,0.14)";
    ctx.lineWidth = 2 * layoutScale;
    ctx.setLineDash([dash * 0.55, dash * 0.45]);
    ctx.lineDashOffset = -offset;
    ctx.beginPath();
    ctx.moveTo(canvasW / 2, -40);
    ctx.lineTo(canvasW / 2, canvasH + 40);
    ctx.stroke();

    ctx.strokeStyle = accentHex;
    ctx.globalAlpha = 0.35;
    ctx.lineWidth = 3 * layoutScale;
    ctx.setLineDash([]);
    ctx.strokeRect(roadX, 0, roadW, canvasH);
    ctx.restore();
}

export function drawHighwayObstacles(
    ctx: CanvasRenderingContext2D,
    canvasW: number,
    canvasH: number,
    layoutScale: number,
    nowMs: number,
) {
    ctx.save();
    ctx.lineCap = "round";

    for (const o of obstacles) {
        if (o.kind === "laser") {
            const shift = o.sweepX ?? 0;
            const pulse = 0.85 + Math.sin(nowMs * 0.008 + o.segs[0]?.y1 * 0.01) * 0.15;
            ctx.shadowBlur = 14 * layoutScale;
            ctx.lineWidth = o.half * 2;
            ctx.strokeStyle = `rgba(255,50,70,${0.92 * pulse})`;
            ctx.shadowColor = "rgba(255,0,60,0.85)";
            for (const s of o.segs) {
                const sy = worldToScreenY(s.y1, canvasH);
                if (sy < -80 || sy > canvasH + 80) continue;
                ctx.beginPath();
                ctx.moveTo(s.x1 + shift, sy);
                ctx.lineTo(s.x2 + shift, worldToScreenY(s.y2, canvasH));
                ctx.stroke();
            }
            continue;
        }

        const sy = worldToScreenY(o.worldY, canvasH);
        if (sy < -120 || sy > canvasH + 120) continue;

        neon(ctx, o.hue, 0.95);
        ctx.shadowBlur = 16 * layoutScale;

        if (o.kind === "cube") {
            const side = o.half * 2;
            ctx.fillRect(o.worldX - o.half, sy - o.half, side, side);
            ctx.shadowBlur = 0;
            ctx.strokeRect(o.worldX - o.half, sy - o.half, side, side);
        } else if (o.kind === "triangle") {
            const h = o.size * 1.15;
            ctx.beginPath();
            ctx.moveTo(o.worldX, sy - h * 0.55);
            ctx.lineTo(o.worldX - o.size, sy + h * 0.45);
            ctx.lineTo(o.worldX + o.size, sy + h * 0.45);
            ctx.closePath();
            ctx.fill();
            ctx.shadowBlur = 0;
            ctx.stroke();
        } else {
            ctx.beginPath();
            ctx.arc(o.worldX, sy, o.r, 0, Math.PI * 2);
            ctx.fill();
        }
    }

    ctx.shadowBlur = 0;
    ctx.restore();
}

/** Draw authoritative highway state scaled from server reference space to the client canvas. */
export function drawHighwayLayerRefMapped(
    ctx: CanvasRenderingContext2D,
    canvasW: number,
    canvasH: number,
    refW: number,
    refH: number,
    layoutScale: number,
    accentHex: string,
    nowMs: number,
) {
    if (refW <= 0 || refH <= 0 || canvasW <= 0 || canvasH <= 0) return;
    ctx.save();
    ctx.scale(canvasW / refW, canvasH / refH);
    drawHighwayRoad(ctx, refW, refH, layoutScale, accentHex);
    drawHighwayObstacles(ctx, refW, refH, layoutScale, nowMs);
    ctx.restore();
}

function circleSegHit(cx: number, cy: number, cr: number, seg: HighwayLaserSeg, shiftX: number): boolean {
    const d = distPointToSeg(cx, cy, seg.x1 + shiftX, seg.y1, seg.x2 + shiftX, seg.y2);
    return d < cr + 0.5;
}

function circleRectHit(cx: number, cy: number, cr: number, rx: number, ry: number, rw: number, rh: number): boolean {
    const nx = clamp(cx, rx, rx + rw);
    const ny = clamp(cy, ry, ry + rh);
    return Math.hypot(cx - nx, cy - ny) < cr;
}

function circleTriangleHit(cx: number, cy: number, cr: number, tx: number, ty: number, size: number): boolean {
    const h = size * 1.15;
    const ax = tx;
    const ay = ty - h * 0.55;
    const bx = tx - size;
    const by = ty + h * 0.45;
    const cx2 = tx + size;
    const cy2 = by;
    const edges: [number, number, number, number][] = [
        [ax, ay, bx, by],
        [bx, by, cx2, cy2],
        [cx2, cy2, ax, ay],
    ];
    for (const [x1, y1, x2, y2] of edges) {
        if (circleSegHit(cx, cy, cr, { x1, y1, x2, y2 }, 0)) return true;
    }
    return false;
}

export function highwayCollides(cursorX: number, cursorY: number, cursorR: number, canvasH: number): boolean {
    for (const o of obstacles) {
        if (o.kind === "laser") {
            const shift = o.sweepX ?? 0;
            for (const s of o.segs) {
                const sy = worldToScreenY(s.y1, canvasH);
                if (sy < -60 || sy > canvasH + 60) continue;
                const seg = { ...s, y1: sy, y2: worldToScreenY(s.y2, canvasH) };
                if (circleSegHit(cursorX, cursorY, cursorR, seg, shift)) return true;
            }
        } else if (o.kind === "cube") {
            const sy = worldToScreenY(o.worldY, canvasH);
            if (circleRectHit(cursorX, cursorY, cursorR, o.worldX - o.half, sy - o.half, o.half * 2, o.half * 2))
                return true;
        } else if (o.kind === "triangle") {
            const sy = worldToScreenY(o.worldY, canvasH);
            if (circleTriangleHit(cursorX, cursorY, cursorR, o.worldX, sy, o.size)) return true;
        } else {
            const sy = worldToScreenY(o.worldY, canvasH);
            if (Math.hypot(cursorX - o.worldX, cursorY - sy) < cursorR + o.r) return true;
        }
    }
    return false;
}

/** True when the cursor circle is fully outside the viewport. */
export function highwayCursorOffScreen(
    cursorX: number,
    cursorY: number,
    cursorR: number,
    canvasW: number,
    canvasH: number,
): boolean {
    return (
        cursorX + cursorR < 0 ||
        cursorX - cursorR > canvasW ||
        cursorY + cursorR < 0 ||
        cursorY - cursorR > canvasH
    );
}

/** Apply authoritative highway snapshot for multiplayer rendering. */
export function applyHighwaySnapshot(snap: {
    cameraY: number;
    obstacles: HighwayObstacle[];
    nextSpawnWorldY?: number;
}) {
    cameraY = snap.cameraY;
    obstacles = Array.isArray(snap.obstacles) ? snap.obstacles : [];
    if (snap.nextSpawnWorldY != null) nextSpawnWorldY = snap.nextSpawnWorldY;
}
